import React, { useRef } from 'react';
import { useTouchActive } from './useTouchActive';

interface MarqueeWordProps {
  children: React.ReactNode;
  /** ink for the light sections, light for the dark contact card */
  tone?: 'ink' | 'light';
  /** start hollow and fill in on hover */
  outlined?: boolean;
  size?: string;
}

/**
 * A single word in a running line. Hovering one word fills it in, lifts it
 * slightly and spins its trailing star, while the marquee itself eases down —
 * so a passing line becomes something you can actually pick a word out of.
 */
const MarqueeWord: React.FC<MarqueeWordProps> = ({
  children,
  tone = 'ink',
  outlined = false,
  size = 'text-3xl md:text-5xl',
}) => {
  const base = outlined
    ? tone === 'ink'
      ? 'mq-outline-ink'
      : 'mq-outline-light'
    : tone === 'ink'
    ? 'mq-solid-ink'
    : 'mq-solid-light';

  // These words travel sideways, so the band is a narrow vertical strip down
  // the middle of the screen: a word fills in as it passes the centre and
  // releases as it leaves, which turns the line into something a thumb-less
  // reader can still watch resolve.
  const ref = useRef<HTMLSpanElement>(null);
  const active = useTouchActive(ref, '0px -46% 0px -46%');

  return (
    <span
      ref={ref}
      className={`group/word inline-flex items-center cursor-default ${active ? 'is-active' : ''}`}
      data-cursor={undefined}
    >
      <span
        className={`${base} ${size} font-grotesk font-semibold tracking-tight whitespace-nowrap inline-block transition-transform duration-300 ease-out group-hover/word:-translate-y-[2px] group-hover/word:scale-[1.04] ${
          active ? '-translate-y-[2px] scale-[1.04]' : ''
        }`}
      >
        {children}
      </span>
      <span
        className={`mx-5 md:mx-8 text-electric text-xl md:text-3xl inline-block transition-transform duration-500 ease-out group-hover/word:rotate-180 group-hover/word:scale-125 ${
          active ? 'rotate-180 scale-125' : ''
        }`}
      >
        ✦
      </span>
    </span>
  );
};

export default MarqueeWord;
